import {
	getCurrentScope,
	onScopeDispose,
	readonly,
	type Ref,
	ref,
} from 'vue';
import {
	callSafe,
	debounce,
} from '../utils/functions';

interface UseViewportReturn {
	fullViewport: Ref<Viewport>;
	visibleViewport: Ref<Viewport>;
	stop: () => void;
}

export type GetViewportWatchCallback = (viewport: Viewport) => void;

export interface Viewport {
	height: number;
	width: number;
}

export type ViewportType = 'full' | 'visible';

interface ViewportState {
	fullViewport: Viewport;
	visibleViewport: Viewport;
	resizeObserver: ResizeObserver;
	watchers: Record<ViewportType, GetViewportWatchCallback[]>;
}

let state: ViewportState | null = null;
let preventVirtualKeyboardScroll = false;

function readFullViewport(): Viewport {
	return {
		width: Math.floor(
			Math.max(
				document.documentElement.clientWidth,
				window.innerWidth || 0,
			),
		),
		height: Math.floor(
			Math.max(
				document.documentElement.clientHeight,
				window.innerHeight || 0,
			),
		),
	};
}

function readVisibleViewport(): Viewport {
	return {
		height: Math.floor(window.visualViewport?.height || 0),
		width: Math.floor(window.visualViewport?.width || 0),
	};
}

function ensureInitialized(): ViewportState {
	if (state) {
		return state;
	}

	const resizeObserver = new ResizeObserver(() => onViewportChange());

	const initialState: ViewportState = {
		fullViewport: readFullViewport(),
		visibleViewport: readVisibleViewport(),
		resizeObserver,
		watchers: {
			full: [],
			visible: [],
		},
	};

	function onViewportChange(): void {
		const currentFullViewport = readFullViewport();
		const currentVisibleViewport = readVisibleViewport();

		if (
			currentFullViewport.height !== initialState.fullViewport.height
			|| currentFullViewport.width !== initialState.fullViewport.width
		) {
			initialState.fullViewport = currentFullViewport;

			for (const watcher of initialState.watchers.full) {
				callSafe(() => watcher(currentFullViewport));
			}
		}

		if (
			currentVisibleViewport.height !== initialState.visibleViewport.height
			|| currentVisibleViewport.width !== initialState.visibleViewport.width
		) {
			initialState.visibleViewport = currentVisibleViewport;

			for (const watcher of initialState.watchers.visible) {
				callSafe(() => watcher(currentVisibleViewport));
			}
		}
	}

	const onViewportChangeDebounce = debounce(
		onViewportChange,
		100,
	);

	resizeObserver.observe(document.documentElement);

	window.visualViewport?.addEventListener(
		'resize',
		() => onViewportChangeDebounce(),
	);
	window.visualViewport?.addEventListener(
		'scroll',
		() => onViewportChangeDebounce(),
	);

	state = initialState;
	return state;
}

function onWebAppScroll(event: TouchEvent): void {
	const watcherState = ensureInitialized();

	if (
		watcherState.visibleViewport.height !== window.innerHeight
		|| watcherState.visibleViewport.width !== window.innerWidth
	) {
		event.preventDefault();
	}
}

export function getFullViewport(): Viewport {
	return ensureInitialized().fullViewport;
}

export function getVisibleViewport(): Viewport {
	return ensureInitialized().visibleViewport;
}

export function watch(
	callback: GetViewportWatchCallback,
	type: ViewportType[] | ViewportType = 'full',
): () => void {
	const watcherState = ensureInitialized();
	const arrayType = (
		Array.isArray(type)
			? type
			: [type]
	);

	for (const singleType of arrayType) {
		watcherState.watchers[singleType].push(callback);
	}

	return () => {
		for (const singleType of arrayType) {
			const index = watcherState.watchers[singleType].indexOf(callback);

			if (index > -1) {
				watcherState.watchers[singleType].splice(
					index,
					1,
				);
			}
		}
	};
}

export function useViewport(): UseViewportReturn {
	const watcherState = ensureInitialized();
	const fullViewportState = ref(watcherState.fullViewport);
	const visibleViewportState = ref(watcherState.visibleViewport);
	const disposers: Array<() => void> = [
		watch((value) => {
			fullViewportState.value = value;
		}),
		watch(
			(value) => {
				visibleViewportState.value = value;
			},
			'visible',
		),
	];

	const stop = () => {
		while (disposers.length) {
			const disposer = disposers.pop();
			disposer?.();
		}
	};

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		fullViewport: readonly(fullViewportState),
		visibleViewport: readonly(visibleViewportState),
		stop,
	};
}

export function configureAppHeight(): void {
	if (
		document.documentElement.style.getPropertyValue('--app-height')
		&& document.documentElement.style.getPropertyValue('--app-full-height')
	) {
		return;
	}

	function applyHeights(): void {
		const watcherState = ensureInitialized();

		if (watcherState.visibleViewport.height) {
			document.documentElement.style.setProperty(
				'--app-height',
				`${watcherState.visibleViewport.height}px`,
			);
		} else {
			document.documentElement.style.removeProperty('--app-height');
		}

		if (watcherState.fullViewport.height) {
			document.documentElement.style.setProperty(
				'--app-full-height',
				`${watcherState.fullViewport.height}px`,
			);
		} else {
			document.documentElement.style.removeProperty('--app-full-height');
		}
	}

	applyHeights();
	watch(
		applyHeights,
		[
			'full',
			'visible',
		],
	);
}

export function preventScrollOnVirtualKeyboard(prevent = true): void {
	if (preventVirtualKeyboardScroll === prevent) {
		return;
	}

	preventVirtualKeyboardScroll = prevent;
	const webAppElement = document.getElementById('webapp');

	if (preventVirtualKeyboardScroll) {
		webAppElement?.addEventListener(
			'touchmove',
			onWebAppScroll,
		);
	} else {
		webAppElement?.removeEventListener(
			'touchmove',
			onWebAppScroll,
		);
	}
}
